import { useQuery } from '@tanstack/react-query';
import { queryDataSource, type QueryResponse } from '../../api/query';
import type { SaKaynak } from './ekranKaynaklari';

/**
 * Bir sorgunun filtre girdisi. 990040'in onceki donem sorgusu `donem`i onceki pencereyle verir;
 * `onceki` ise onceki pencereyi PARAMETRE olarak alan ds'lere (990051, 990055) gider.
 */
export interface SaFiltre {
  donem: { bas: string; bit: string };
  sube: string | null;
  kanal: string | null;
  /** K10: takvime hizali onceki pencere — tek kaynak `oncekiDonemHesapla`. */
  onceki?: { bas: string; bit: string } | null;
}

export function parametreBildiriyor(
  kaynak: SaKaynak | undefined,
  anahtar: '@LOKASYON' | '@KANAL' | '@ONCEKIBAS',
): boolean {
  return !!kaynak?.parameters.some((p) => p.key.toUpperCase() === anahtar);
}

/**
 * Ekranin TEK parametre kurali. Tarihler her zaman; `@LOKASYON`/`@KANAL` yalniz ds BILDIRIYORSA ve deger
 * doluysa. Bildirilmemis parametre SQL'de baglanmaz (Postgres 42883, spec §2.7); bos deger gonderilmez
 * cunku ds `DefaultValue ""` ile "tumu" demektir — iki yazim ayni sonuca iki onbellek girdisi acardi.
 * K10: `@ONCEKIBAS`/`@ONCEKIBIT` bildiren ds'e `onceki` verilmisse HER ZAMAN gider; gonderilmezse SQL
 * "hemen onceki esit pencere" yedegine duser ve kartlarla ayni pencere garanti edilmez.
 */
export function saParametreleri(kaynak: SaKaynak, f: SaFiltre): Record<string, string> {
  const p: Record<string, string> = { '@BASTARIH': f.donem.bas, '@BITTARIH': f.donem.bit };
  if (f.sube && parametreBildiriyor(kaynak, '@LOKASYON')) p['@LOKASYON'] = f.sube;
  if (f.kanal && parametreBildiriyor(kaynak, '@KANAL')) p['@KANAL'] = f.kanal;
  if (f.onceki && parametreBildiriyor(kaynak, '@ONCEKIBAS')) {
    p['@ONCEKIBAS'] = f.onceki.bas;
    p['@ONCEKIBIT'] = f.onceki.bit;
  }
  return p;
}

/**
 * TEK queryKey semasi. Ayni ds + ayni parametre = ayni anahtar = tek istek; bu yuzden 990040'i
 * OzetSerit, KanalBlok, SubeBlok, ZamanBlok ve useSubeler PAYLASIR (spec §4.4: ilk acilis 7 istek).
 */
export function saQueryKey(sourceId: number | undefined, parametreler: Record<string, string>) {
  return ['sa', sourceId, parametreler] as const;
}

export function useSaHamSorgu<T>(
  kaynak: SaKaynak | undefined,
  parametreler: Record<string, string>,
  enabled = true,
) {
  return useQuery<QueryResponse<T>>({
    queryKey: saQueryKey(kaynak?.sourceId, parametreler),
    queryFn: () => queryDataSource<T>({ sourceId: kaynak!.sourceId, parameters: parametreler }),
    enabled: !!kaynak && enabled,
    // Bir kaynak duserse ekran tumden cokmesin, o blok bos kalsin (mevcut bloklarla ayni).
    throwOnError: false,
  });
}

export function useSaSorgu<T>(kaynak: SaKaynak | undefined, f: SaFiltre, enabled = true) {
  const parametreler = kaynak ? saParametreleri(kaynak, f) : {};
  return useSaHamSorgu<T>(kaynak, parametreler, enabled);
}
